import { Button, Popover } from 'antd'
import clsx from 'clsx'
import type { Dispatch, SetStateAction } from 'react'
import { useMemo } from 'react'

import CopyCode from '@/assets/copy-code-light.png'
import CopyIcon from '@/assets/copy.png'
import { useLocalStorage } from '@/context/localstorage-context'
import { handleCopyCode } from '@/utils'

function Content({ wkAddr }: { wkAddr: string }) {
  return (
    <div className="flex justify-between gap-4 items-center dark:text-white text-light-0">
      <span>{wkAddr}</span>
      <div className="bg-light-888 p-4 rounded-2xl dark:bg-dark-106">
        <img className="cursor-pointer" src={CopyIcon} width={18} />
      </div>
    </div>
  )
}

export default function StepThree({
  canRegist,
  setStep,
  wkAddr,
}: {
  wkAddr: string
  canRegist: boolean
  setStep: Dispatch<SetStateAction<number>>
}) {
  const { theme } = useLocalStorage()
  const isDark = useMemo(() => theme === 'dark', [theme])

  return (
    <div>
      <div className="border-b-1 border-b-light-555 dark:border-b-dark-666">
        <p className="pb-[10px] text-3xl text-light-111 mb-[0] dark:text-dark-111">
          Set Up Bridge Node
        </p>
      </div>
      <div className="flex mt-[15px]">
        <div className="w-[10px] mr-4 dark:text-white">1.</div>
        <div className="flex-1 font-[16px] flex flex-col gap-2">
          <span className="dark:text-white font-semibold">
            Transfer WAN for Gas Fees
          </span>
          <div className="dark:text-white text-[12px]">
            Ensure a small amount of WAN (e.g. 20 WAN) is transferred to your{' '}
            <span className="font-semibold">Work Address</span> for gas fees.
            Transactions will fail if the Work Address balance is{' '}
            <span className="font-semibold">0 WAN</span>.
          </div>
          <Popover
            arrowPointAtCenter={true}
            color={isDark ? '#092437' : '#fff'}
            overlayInnerStyle={{ borderRadius: '12px', color: '#fff' }}
            content={<Content wkAddr={wkAddr} />}
          >
            <span className="text-light-111 dark:text-dark-111 w-fit cursor-pointer">
              View Your Work Address
            </span>
          </Popover>
        </div>
      </div>
      <div className="flex mt-[15px]">
        <div className="w-[10px] mr-4 dark:text-white">2.</div>
        <div className="flex-1 font-[16px] flex flex-col gap-2">
          <span className="dark:text-white font-semibold">
            Environment Setup
          </span>
          <div className="mb-1 text-[12px] ">
            <div className="text-light-0 mb-1 dark:text-white">
              Log in to your cloud server (e.g., AWS) and configure firewall
              settings:
            </div>
            <ul className="list-disc ml-5 text-light-0 dark:text-white">
              <li>
                <strong>Inbound:</strong> TCP 37718 / UDP 37718
              </li>
              <li>
                <strong>Outbound:</strong> TCP 26891, TCP 26892, TCP 30000
              </li>
            </ul>
            <div className="text-light-0 dark:text-white">
              Default outbound settings on most platforms are open; keep default
              if so. If you modify any port numbers in the scripts, add
              corresponding firewall rules.
            </div>
          </div>
        </div>
      </div>
      <div className="flex mt-[15px]">
        <div className="w-[10px] mr-4 dark:text-white">3.</div>
        <div className="flex-1 font-[16px] flex flex-col gap-2 w-full rounded-xl">
          <div className="flex items-center gap-2">
            <span className="dark:text-white font-semibold">
              Initialize Environment
            </span>
            <img
              src={CopyCode}
              alt="Copy code"
              className="w-5 h-5 object-contain flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => handleCopyCode('initEnv')}
            />
          </div>
          <div className="text-[12px]">
            <pre
              id="initEnv"
              className="flex flex-col text-light-111 overflow-auto whitespace-pre-wrap break-words dark:text-dark-111"
            >
              <code className="whitespace-pre-wrap break-words">
                sudo chown $USER ~/osm
              </code>
              <code className="whitespace-pre-wrap break-words">cd ~/osm</code>
              <code className="whitespace-pre-wrap break-words">
                rm init_open_storeman_EnvV3.sh
              </code>
              <code className="whitespace-pre-wrap break-words">
                wget
                https://raw.githubusercontent.com/wanchain/scripts/refs/heads/main/storeman/mainnet/init_open_storeman_EnvV3.sh
                && chmod +x init_open_storeman_EnvV3.sh &&
                ./init_open_storeman_EnvV3.sh
              </code>
              <code className="whitespace-pre-wrap break-words">
                rm startStoremanV5.sh
              </code>
              <code className="whitespace-pre-wrap break-words">
                wget
                https://raw.githubusercontent.com/wanchain/scripts/refs/heads/main/storeman/mainnet/startStoremanV5.sh
                && chmod +x startStoremanV5.sh
              </code>
            </pre>
          </div>
        </div>
      </div>
      <div className="flex">
        <div className="w-[10px] mr-4 dark:text-white">4.</div>
        <div className="flex-1 font-[16px] flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="dark:text-white font-semibold">
              Start Storeman Agent
            </span>
            <img
              src={CopyCode}
              alt="Copy code"
              className="w-5 h-5 object-contain flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => handleCopyCode('startAgent')}
            />
          </div>
          <div className="text-[12px]">
            <pre
              id="startAgent"
              className="flex flex-col text-light-111 mb-1 overflow-x-auto rounded-xl dark:text-dark-111"
            >
              <code>cd ~/osm/</code>
              <code>./startStoremanV5.sh</code>
            </pre>
          </div>
          <div>
            <div className="bg-light-888 p-4 rounded-xl text-[12px] dark:bg-dark-333">
              <div className="text-light-0 dark:text-dark-0">
                Follow on-screen instructions to:
              </div>
              <ul className="list-disc ml-5 text-light-0 mb-0 dark:text-dark-0">
                <li>Choose mainnet</li>
                <li>
                  Enter your <strong>Work Address</strong>
                </li>
                <li>
                  Enter your <strong>Work Address</strong> password
                </li>
                <li>Choose whether to use KMS for private-key encryption</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="flex mt-[10px]">
        <div className="w-[10px] mr-4 dark:text-white">5.</div>
        <div className="flex-1 font-[16px] flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="dark:text-white font-semibold">
              Check Agent Container Status
            </span>
            <img
              src={CopyCode}
              alt="Copy code"
              className="w-5 h-5 object-contain flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => handleCopyCode('checkAgent')}
            />
          </div>
          <div className="text-[12px]">
            <pre
              id="checkAgent"
              className="text-light-111 rounded-xl mb-2 overflow-x-auto dark:text-dark-111"
            >
              <code>sudo docker exec -it openstoreman_mainnet pm2 l</code>
            </pre>
            <div className="bg-light-888 p-4 rounded-xl text-light-0 dark:bg-dark-333 dark:text-white">
              If status = online and restart = 0, the service is running
              normally.
            </div>
          </div>
        </div>
      </div>

      <div className="flex mt-[10px]">
        <div className="w-[10px] mr-4 dark:text-white">6.</div>
        <div className="flex-1 font-[16px] flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="dark:text-white font-semibold">
              Verify Storeman Peer Connections
            </span>
            <img
              src={CopyCode}
              alt="Copy code"
              className="w-5 h-5 object-contain flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => handleCopyCode('verfiyPeer')}
            />
          </div>
          <div className="text-[12px]">
            <pre
              id="verfiyPeer"
              className="flex flex-col text-light-111 overflow-x-auto mb-[5px]"
            >
              <code className="whitespace-pre-wrap break-words dark:text-dark-111">
                sudo docker exec -it openstoreman_mainnet
                ./schnorrmpc/bin/schnorrmpc attach ./schnorrmpc/data/gwan.ipc
              </code>
              <code>admin.peers.length</code>
            </pre>
            <div className="text-light-0 dark:text-white">
              Confirm that the returned peer node count is either 1 or 25
            </div>
            <div className="bg-light-888 p-4 rounded-xl mt-2 dark:bg-dark-333">
              <div className="text-light-0 mb-1 dark:text-dark-0">
                <strong>Note:</strong>
              </div>
              <ul className="list-disc ml-5 text-light-0 mb-0 dark:text-dark-0">
                <li>Selecting phase: peer number = 1</li>
                <li>Ready phase: peer number = 25</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="flex mt-[10px]">
        <div className="w-[10px] mr-4 dark:text-white">7.</div>
        <div className="flex-1 font-[16px] flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className="dark:text-white font-semibold">
              Restart Service <span className="text-light-444">(Optional)</span>
            </span>
            <img
              src={CopyCode}
              alt="Copy code"
              className="w-5 h-5 object-contain flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => handleCopyCode('restart')}
            />
          </div>
          <div className="text-[12px]">
            <div className="text-light-0 dark:text-white">
              If the node behaves abnormally, restart:
            </div>
            <pre
              id="restart"
              className="flex flex-col text-light-111 mt-2 overflow-x-auto dark:text-dark-111"
            >
              <code>cd ~/osm/</code>
              <code>./startStoremanV5.sh</code>
            </pre>
          </div>
        </div>
      </div>

      <div className="text-[14px] dark:text-white">
        Once completed, your Bridge Node service is active and running normally.
      </div>

      <div
        className={clsx('w-full flex justify-around mt-[20px]', {
          'btn1-light': !isDark,
          'btn1-dark': isDark,
        })}
      >
        <Button
          disabled={true}
          onClick={() => setStep(11)}
          className="w-[45%] rounded-2xl h-[60px] text-[18px]"
        >
          Previous
        </Button>
        <Button
          disabled={!canRegist}
          className="w-[45%] rounded-2xl h-[60px] btn text-[18px]"
          onClick={() => setStep(3)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
